// Version: 20260102-193000-react-dedupe-isolated
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { initializeSentry } from './config/sentry.js'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './context/ThemeContext.jsx'
import ErrorBoundary from './components/ErrorBoundary'
import App from './App.jsx'
import './index.css'
import './styles/typography.css'
import { registerServiceWorker, unregisterServiceWorker } from './utils/pwa.js'

// Initialize Sentry FIRST before anything else
initializeSentry();

// ─── Global Error Handlers ─────────────────────────────────────────────────
// Catch unhandled promise rejections (async errors with no .catch())
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason || 'Unknown async error');

  // Ignore benign framework / extension noise
  if (
    message.includes('ResizeObserver') ||
    message.includes('Loading chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('_retry')
  ) {
    return;
  }

  console.error('[Unhandled Rejection]', reason);

  // Capture in Sentry
  import('./config/sentry.js').then(({ captureException }) => {
    captureException(reason instanceof Error ? reason : new Error(message), {
      source: 'unhandledrejection',
    });
  }).catch(() => { /* Sentry unavailable — non-fatal */ });

  // Show toast (lazy — avoid circular import at module init)
  import('./utils/toast.js').then(({ default: toast }) => {
    toast.error('An unexpected error occurred. Our team has been notified.', {
      duration: 6000,
      deduplicate: true,
    });
  }).catch(() => {});
});

// Catch synchronous uncaught errors (rare in React apps but covers Web Workers / eval)
window.addEventListener('error', (event) => {
  // Only handle script errors, not resource load errors (images, stylesheets)
  if (event.target !== window) return;

  const message = event.message || '';
  if (
    message.includes('ResizeObserver') ||
    message.includes('Script error') // cross-origin scripts — no useful info
  ) {
    return;
  }

  console.error('[Uncaught Error]', event.error || event.message);
});

// Create emotion cache
const emotionCache = createCache({
  key: 'css',
  prepend: true,
})

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // Data stays fresh for 30 seconds
      cacheTime: 5 * 60 * 1000, // Cache data for 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
})

// Render app
const rootElement = document.getElementById('root');
if (rootElement) {
  // Filter out recoverable errors from browser extensions and third-party scripts
  // These are commonly caused by extensions like Datadog RUM, Intercom, etc.
  const onRecoverableError = (error, errorInfo) => {
    // Ignore hydration errors caused by browser extensions
    const errorString = error?.toString?.() || '';
    const isExtensionError = 
      errorString.includes('#418') || // Hydration mismatch (often from extensions)
      errorString.includes('#423') || // Text content mismatch
      errorString.includes('#425') || // Hydration failed
      errorString.includes('moveNode') || // DOM manipulation by extensions
      errorString.includes('backend-api') || // External telemetry
      errorString.includes('telemetry'); // External telemetry
    
    if (isExtensionError) {
      // Silently ignore extension-caused errors in production
      if (import.meta.env.DEV) {
        console.debug('[React Recoverable] Suppressed extension error:', error.message);
      }
      return;
    }
    
    // Log other recoverable errors
    console.error('React recoverable error:', error, errorInfo);
  };

  ReactDOM.createRoot(rootElement, { onRecoverableError }).render(
    <React.StrictMode>
      <CacheProvider value={emotionCache}>
        <ErrorBoundary>
          <ThemeProvider>
            <QueryClientProvider client={queryClient}>
              <Sentry.Profiler name="App">
                <App />
              </Sentry.Profiler>
            </QueryClientProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </CacheProvider>
    </React.StrictMode>,
  );
}

// Register service worker only in production; ensure dev stays SW-free
if (import.meta.env.PROD) {
  registerServiceWorker();
} else {
  unregisterServiceWorker();
}
// DEPLOYMENT_FORCE: Fri Jan  2 08:20:33 UTC 2026
